import { analyzeContent, getStructureRecommendations, ContentAnalysis } from './content-intent-detector';

// Types for structured data
interface SnippetData {
  keywords: {
    missing: string[];
    present: string[];
    seo: string[];
  };
  headers: string[];
  text: string;
  result: string;
  description: string;
  // Нові поля для розумної генерації
  language?: string;
  keyword?: string;
  serpResults?: any[];
  contentAnalysis?: ContentAnalysis;
}

// Global state to store snippet data
let snippetData: SnippetData = {
  keywords: {
    missing: [],
    present: [],
    seo: []
  },
  headers: [],
  text: '',
  result: '',
  description: '',
  language: 'uk',
  keyword: '',
  serpResults: [],
  contentAnalysis: undefined
};

// Overrides for user-selected values
let languageOverride: string | null = null;
let contentTypeOverride: string | null = null;
let intentOverride: string | null = null;

// Function to set overrides
export function setContentOverrides(overrides: {
  language?: string;
  contentType?: string;
  intent?: string;
}) {
  if (overrides.language) languageOverride = overrides.language;
  if (overrides.contentType) contentTypeOverride = overrides.contentType;
  if (overrides.intent) intentOverride = overrides.intent;
  console.log('🎯 Content overrides set:', { languageOverride, contentTypeOverride, intentOverride });
}

// Function to update snippet data
export function updateSnippetData(data: Partial<SnippetData>) {
  console.log('🟡 UPDATE SNIPPET DATA - Updating snippet data:', data);
  
  // Deep merge for keywords
  if (data.keywords) {
    snippetData.keywords = {
      ...snippetData.keywords,
      ...data.keywords
    };
    console.log('🟡 UPDATE SNIPPET DATA - Updated keywords:', snippetData.keywords);
  }

  // Update other fields if provided
  if (data.headers) {
    snippetData.headers = data.headers;
  }
  if (data.text !== undefined) {
    snippetData.text = data.text;
  }
  if (data.result !== undefined) {
    snippetData.result = data.result;
  }
  if (data.description !== undefined) {
    snippetData.description = data.description;
  }
  if (data.language !== undefined) {
    snippetData.language = data.language;
  }
  if (data.keyword !== undefined) {
    snippetData.keyword = data.keyword;
  }
  if (data.serpResults !== undefined) {
    snippetData.serpResults = data.serpResults;
  }

  // Автоматично проводимо аналіз контенту якщо є достатньо даних
  if (snippetData.keyword && snippetData.language) {
    snippetData.contentAnalysis = analyzeContent({
      keyword: snippetData.keyword,
      language: snippetData.language,
      serpResults: snippetData.serpResults || []
    });
    console.log('🟡 UPDATE SNIPPET DATA - Content analysis:', snippetData.contentAnalysis);
  }

  // Log updated state
  console.log('🟡 UPDATE SNIPPET DATA - Updated snippet data:', snippetData);
}

// Function to get formatted keyword lists
function getFormattedKeywords(keywords: string[], type: string): string {
  if (!keywords || keywords.length === 0) {
    return `No ${type} keywords available`;
  }

  // Filter out empty strings and duplicates
  const uniqueKeywords = [...new Set(keywords.filter(k => k.trim()))];
  
  // Format based on type
  switch (type) {
    case 'missing':
      return `Missing Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    case 'present':
      return `Present Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    case 'seo':
      return `SEO Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    default:
      return uniqueKeywords.join(', ');
  }
}

// Function to get formatted headers
function getFormattedHeaders(headers: string[]): string {
  if (!headers || headers.length === 0) {
    return 'No competitor headers available';
  }

  return headers
    .filter(h => h.trim())
    .map(h => `- ${h}`)
    .join('\n');
}

export function replaceSnippets(prompt: string): string {
  try {
    console.log('🔵 REPLACE SNIPPETS - Replacing snippets in prompt:', prompt);
    console.log('🔵 REPLACE SNIPPETS - Current snippet data:', snippetData);
    console.log('🔵 REPLACE SNIPPETS - Missing keywords:', snippetData.keywords.missing);
    console.log('🔵 REPLACE SNIPPETS - Present keywords:', snippetData.keywords.present);

    // Отримуємо структурні рекомендації якщо є аналіз
    const structureRecs = snippetData.contentAnalysis 
      ? getStructureRecommendations(snippetData.contentAnalysis)
      : null;

    // Використовуємо overrides якщо вони є
    const finalLanguage = languageOverride || snippetData.language || 'uk';
    const finalContentType = contentTypeOverride || snippetData.contentAnalysis?.contentType || 'general';
    const finalIntent = intentOverride || snippetData.contentAnalysis?.intent || 'informational';

    // Створюємо детальну інструкцію про мову
    const languageNames: Record<string, string> = {
      'uk': 'Ukrainian (українська мова)',
      'ru': 'Russian (русский язык)',
      'en': 'English'
    };
    const languageInstruction = `CRITICAL LANGUAGE REQUIREMENT: You MUST write EXCLUSIVELY in ${languageNames[finalLanguage] || finalLanguage}. Every single word, sentence, and paragraph must be in ${languageNames[finalLanguage]}. Do not mix languages under any circumstances!`;

    // Create replacement map
    const replacements: Record<string, string> = {
      '{{Missing words}}': getFormattedKeywords(snippetData.keywords.missing, 'missing'),
      '{{Present words}}': getFormattedKeywords(snippetData.keywords.present, 'present'),
      '{{Keywords}}': getFormattedKeywords(snippetData.keywords.seo, 'seo'),
      '{{Headers}}': getFormattedHeaders(snippetData.headers),
      '{{Text}}': snippetData.text || 'No content available',
      '{{Resultai}}': snippetData.result || 'No result available',
      '{{Description}}': snippetData.description || 'No description available',
      // Нові розумні placeholders з урахуванням overrides
      '{{Language}}': languageInstruction,
      '{{Keyword}}': snippetData.keyword || '',
      '{{Intent}}': finalIntent,
      '{{ContentType}}': finalContentType,
      '{{StructureRecommendations}}': structureRecs 
        ? `Recommended sections:\n${structureRecs.recommendedSections.map(s => `- ${s}`).join('\n')}\n\nPriority elements:\n${structureRecs.priorityElements.map(s => `- ${s}`).join('\n')}`
        : 'No structure recommendations available',
      '{{CompetitorInsights}}': snippetData.contentAnalysis
        ? `Dominant content type: ${snippetData.contentAnalysis.competitorPatterns.dominantType}\nE-commerce presence: ${snippetData.contentAnalysis.competitorPatterns.hasEcommerce ? 'Yes' : 'No'}\nBlog presence: ${snippetData.contentAnalysis.competitorPatterns.hasBlog ? 'Yes' : 'No'}`
        : 'No competitor insights available'
    };

    console.log('🔵 REPLACE SNIPPETS - Replacements:', replacements);

    // Replace all snippets
    let processedPrompt = prompt;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      const regex = new RegExp(escapeRegExp(placeholder), 'g');
      processedPrompt = processedPrompt.replace(regex, value);
    });

    console.log('🔵 REPLACE SNIPPETS - Processed prompt:', processedPrompt);
    return processedPrompt;

  } catch (error) {
    console.error('Error replacing snippets:', error);
    return prompt;
  }
}

// Helper function to escape special characters in regex
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}